/**
 * Testimonials Component
 * 
 * Displays user testimonials/feedback with appropriate disclaimers.
 * Uses placeholder testimonials that represent typical user experiences.
 * 
 * Dark theme: Deep navy background with gold accents
 */

import { Quote } from "lucide-react";

// Dark theme color palette - extracted from ClauseClear logo
const colors = {
  background: '#2E334A',
  backgroundLight: '#3A3F54',
  backgroundSection: '#252938',
  text: '#FFFFFF',
  textMuted: '#B8BCC8',
  textDim: '#8A8F9E',
  accent: '#D4AF37',
  border: '#3E4459',
};

const testimonials = [
  {
    id: 1,
    quote: "ClauseClear helped me understand my employment contract before speaking to a lawyer. I felt much more prepared and knew exactly which questions to ask.",
    context: "First-time employee reviewing a contract",
  },
  {
    id: 2,
    quote: "It explained clauses in plain English without trying to sell legal services. Finally, a tool that just helps you understand what you are signing.",
    context: "Freelance consultant",
  },
  {
    id: 3,
    quote: "Made it easier to decide when I actually needed professional advice. I saved time and money by knowing which clauses were standard and which needed attention.",
    context: "Small business owner",
  },
];

export default function Testimonials() {
  return (
    <section 
      data-testid="testimonials-section"
      className="py-20 px-6 md:px-12 lg:px-24"
      style={{ 
        backgroundColor: colors.backgroundSection,
        borderTop: `1px solid ${colors.border}`,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-px" style={{ backgroundColor: colors.accent }} />
            <h2 
              className="text-3xl md:text-4xl font-medium tracking-tight"
              style={{ color: colors.text }}
            >
              Trusted by Real People
            </h2>
            <div className="w-12 h-px" style={{ backgroundColor: colors.accent }} />
          </div>
          <p 
            className="text-lg max-w-2xl mx-auto"
            style={{ color: colors.textMuted }}
          >
            Everyday people using ClauseClear to understand their contracts better
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              data-testid={`testimonial-${testimonial.id}`}
              className="rounded-xl p-8 relative"
              style={{ 
                backgroundColor: colors.backgroundLight, 
                border: `1px solid ${colors.border}` 
              }}
            >
              {/* Quote Icon */}
              <div 
                className="absolute -top-4 left-6 p-2 rounded-lg"
                style={{ backgroundColor: colors.background }}
              >
                <Quote className="w-5 h-5" style={{ color: colors.accent }} />
              </div>

              {/* Quote Text */}
              <blockquote 
                className="text-base leading-relaxed mb-6 pt-4"
                style={{ color: colors.textMuted }}
              >
                {testimonial.quote}
              </blockquote>

              {/* Context */}
              <p 
                className="text-sm font-medium"
                style={{ color: colors.textDim }}
              >
                — {testimonial.context}
              </p>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="text-center">
          <p 
            className="text-xs leading-relaxed max-w-2xl mx-auto"
            style={{ color: colors.textDim }}
          >
            Testimonials shown are representative examples for illustrative purposes. 
            ClauseClear is an educational tool and does not provide legal advice.
          </p>
        </div>
      </div>
    </section>
  );
}
